import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Truck, CreditCard, Package } from "react-feather";
import DataTable from "../../../../../components/common/DataTable";
import ColumnVisibilityMenu from "../../../../../components/common/ColumnVisibilityMenu";
import StatusBadge from "../../../../../components/common/StatusBadge";
import InfoTile from "../../../../../components/common/InfoTile";
import DateRangePicker, { useDatePresetRange } from "../../../../../components/common/DateRangePicker";
import ListExportToolbar from "../../../../stock/components/list/ListExportToolbar";
import { useBranchFilter } from "../../../../branchFilter/useBranchFilter";
import { useHasPermission } from "../../../../auth/useClaims";
import { useGetVehiclesQuery, useListMaintenanceRequestsQuery, useGetMaintenanceRequestQuery } from "../../vehiclesApi";
import { useListLposQuery, useGetLpoQuery, useListStockMovementReportQuery } from "../../../../stock/stockApi";
import { useGetPettyCashVoucherQuery } from "../../../../pettycash/pettyCashApi";
import VehicleRequestDetailCard from "../../components/VehicleRequestDetailCard";
import VehicleTypeSelect from "../../components/VehicleTypeSelect";
import { vehicleTypeLabel } from "../../vehicleTypes";
import { paths } from "../../../../../routes/paths";

const money = (n) => (n === null || n === undefined ? "-" : Number(n).toFixed(2));

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "SUBMITTED", label: "Submitted" },
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Rejected" },
];

const SOURCE_BADGE = {
  MAINTENANCE: { label: "Maintenance", cls: "badge-linesecondary" },
  PETTY_CASH: { label: "Petty Cash", cls: "badge-linesuccess" },
  LPO: { label: "LPO", cls: "badge-lineinfo" },
  STOCK_IN: { label: "Spares Received", cls: "badge-linewarning" },
};

// Renders a single maintenance request's full detail (fetched lazily - the
// list endpoint doesn't carry spareItems) inside an expanded table row.
// A plain function component, not a shared "Loader" - antd only mounts
// `expandable.expandedRowRender` for a row once it's actually expanded, so
// this hook only ever fires on demand, same as any other on-click detail
// fetch in this app.
const ExpandedRequestRow = ({ recordGuid }) => {
  const { data } = useGetMaintenanceRequestQuery(recordGuid);
  return (
    <div className="p-2">
      <VehicleRequestDetailCard request={data} />
    </div>
  );
};

// Headless "subscribe and report up" components - RTK Query already
// dedupes/caches by (endpoint, arg), so mounting one of these per needed
// guid is cheap and avoids the "can't call a hook in a loop" problem of
// fetching a dynamic list of records by id. Rendered only while a single
// vehicle is selected (History mode), where the guid count is small
// (one vehicle's own lifetime of requests/vouchers/LPOs).
const VoucherLoader = ({ recordGuid, onLoaded }) => {
  const { data } = useGetPettyCashVoucherQuery(recordGuid);
  useEffect(() => {
    if (data) onLoaded(recordGuid, data);
  }, [data, recordGuid, onLoaded]);
  return null;
};

const RequestDetailLoader = ({ recordGuid, onLoaded }) => {
  const { data } = useGetMaintenanceRequestQuery(recordGuid);
  useEffect(() => {
    if (data) onLoaded(recordGuid, data);
  }, [data, recordGuid, onLoaded]);
  return null;
};

const LpoDetailLoader = ({ recordGuid, onLoaded }) => {
  const { data } = useGetLpoQuery(recordGuid);
  useEffect(() => {
    if (data) onLoaded(recordGuid, data);
  }, [data, recordGuid, onLoaded]);
  return null;
};

// Vehicle Maintenance Report / History (2026-09-03) - built entirely from
// existing endpoints (vehicles/requests, stock/lpos, stock/movement-report,
// pettycash/vouchers), no new API/DB. Two modes on one screen, switched by
// the Vehicle filter, per the "minimum clicks" brief:
//  - No vehicle selected: a flat, filterable report across every vehicle
//    (same shape as Stock Movement Report - date/branch/status filters,
//    DataTable, export, expandable rows for request detail).
//  - One vehicle selected: Summary KPI cards + a unified chronological
//    History timeline (Maintenance/Petty Cash/LPO/Spares Received) + a
//    Spare Parts breakdown, each row traceable back to its real record.
//
// Relationship strength (see project analysis): Request->Voucher is a real
// FK (PettyCashVoucherRecordGuid, cash-ask path only). Request->LPO is a
// real FK (App_LPO.VehicleRequestRecordGuid). LPO->Stock-In is a real FK
// (LpoId/LpoLineId), read here via the existing Stock Movement Report
// filtered client-side by LPO number. There is NO reliable link from an
// LPO's own CASH-payment auto-created Petty Cash voucher back to that LPO
// (its Description is free text, not a stored id) - deliberately not
// joined on here, so "Total Maintenance Cost" below never double-counts
// that untraceable voucher against the LPO's own Net Amount.
const VehicleMaintenanceReportPage = () => {
  const { selectedBranchId, availableBranches } = useBranchFilter();
  const { preset, setPreset, dateFrom, setDateFrom, dateTo, setDateTo } = useDatePresetRange("LAST_30_DAYS");
  const [vehicleId, setVehicleId] = useState("");
  // Vehicle type (Batch191) - narrows both the vehicle picker and the request
  // list itself (server-side, ?vehicleType=), so the flat report's totals are
  // for that type only. Changing the type drops a vehicle pick that no longer fits.
  const [vehicleType, setVehicleTypeState] = useState("");
  const setVehicleType = (type) => {
    setVehicleTypeState(type);
    setVehicleId((current) => {
      if (!current || !type) return current;
      const picked = vehicles.find((v) => String(v.vehicleId) === String(current));
      return picked && picked.vehicleType !== type ? "" : current;
    });
  };
  const [status, setStatus] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const toggleColumn = (key) => setHiddenColumns((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const canViewLpo = useHasPermission("Stock.ViewLpo");
  const canViewPettyCash = useHasPermission("PettyCash.View");
  const canViewStockMovement = useHasPermission("Reports.StockMovement");

  const { data: vehicles = [] } = useGetVehiclesQuery({ branchId: selectedBranchId ?? undefined });
  const pickableVehicles = vehicleType ? vehicles.filter((v) => v.vehicleType === vehicleType) : vehicles;

  const { data: requestsPage, isLoading } = useListMaintenanceRequestsQuery({
    branchId: selectedBranchId ?? undefined,
    status: status || undefined,
    vehicleId: vehicleId ? Number(vehicleId) : undefined,
    vehicleType: vehicleType || undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    pageSize: 200,
  });
  const requests = requestsPage?.items || [];
  const totalCount = requestsPage?.totalCount ?? requests.length;
  const isTruncated = totalCount > requests.length;

  // Every LPO raised for Vehicle Maintenance, in scope - cross-referenced
  // client-side against each request's own RecordGuid, the exact same
  // established pattern usePendingVehicleRequests.js already uses for the
  // LPO-conversion picker (not a new convention invented for this report).
  const { data: lpoPage } = useListLposQuery(
    { purposeType: "VEHICLE_MAINTENANCE", branchId: selectedBranchId ?? undefined, pageSize: 200 },
    { skip: !canViewLpo }
  );
  const lposByRequestGuid = useMemo(() => {
    const map = new Map();
    (lpoPage?.items || []).forEach((lpo) => {
      if (!lpo.vehicleRequestRecordGuid) return;
      const list = map.get(lpo.vehicleRequestRecordGuid) || [];
      list.push(lpo);
      map.set(lpo.vehicleRequestRecordGuid, list);
    });
    return map;
  }, [lpoPage]);

  const filteredRows = requests.filter(
    (r) =>
      !search ||
      r.requestNumber?.toLowerCase().includes(search.toLowerCase()) ||
      r.regNo?.toLowerCase().includes(search.toLowerCase()) ||
      r.serviceRepairDescription?.toLowerCase().includes(search.toLowerCase())
  );

  // ---- History mode (exactly one vehicle selected) ----
  const isHistoryMode = Boolean(vehicleId);
  const selectedVehicle = vehicles.find((v) => String(v.vehicleId) === String(vehicleId));

  const [vouchersByGuid, setVouchersByGuid] = useState({});
  const [requestDetailsByGuid, setRequestDetailsByGuid] = useState({});
  const [lpoDetailsByGuid, setLpoDetailsByGuid] = useState({});
  const onVoucherLoaded = React.useCallback((guid, data) => setVouchersByGuid((prev) => ({ ...prev, [guid]: data })), []);
  const onRequestDetailLoaded = React.useCallback((guid, data) => setRequestDetailsByGuid((prev) => ({ ...prev, [guid]: data })), []);
  const onLpoDetailLoaded = React.useCallback((guid, data) => setLpoDetailsByGuid((prev) => ({ ...prev, [guid]: data })), []);

  const historyRequests = useMemo(() => (isHistoryMode ? filteredRows : []), [isHistoryMode, filteredRows]);
  const historyLpos = useMemo(
    () => historyRequests.flatMap((r) => lposByRequestGuid.get(r.recordGuid) || []),
    [historyRequests, lposByRequestGuid]
  );
  const voucherGuidsNeeded = useMemo(
    () =>
      canViewPettyCash
        ? [...new Set(historyRequests.filter((r) => r.pettyCashVoucherRecordGuid).map((r) => r.pettyCashVoucherRecordGuid))]
        : [],
    [historyRequests, canViewPettyCash]
  );
  const requestGuidsNeeded = useMemo(() => historyRequests.map((r) => r.recordGuid), [historyRequests]);
  const lpoGuidsNeeded = useMemo(() => [...new Set(historyLpos.map((l) => l.recordGuid))], [historyLpos]);

  // Actual received spares - LPO -> Stock-In is a real FK, surfaced here by
  // reusing the existing Stock Movement Report endpoint (already generic,
  // already exposes lpoNumber on its STOCK_IN rows) filtered client-side to
  // this vehicle's own linked LPO numbers, rather than adding a new endpoint.
  const linkedLpoNumbers = useMemo(() => [...new Set(historyLpos.map((l) => l.lpoNumber))], [historyLpos]);
  const { data: movementPage } = useListStockMovementReportQuery(
    { branchId: selectedBranchId ?? undefined, movementType: "STOCK_IN", dateFrom: dateFrom || undefined, dateTo: dateTo || undefined, pageSize: 500 },
    { skip: !canViewStockMovement || !isHistoryMode || linkedLpoNumbers.length === 0 }
  );
  const stockInDocs = useMemo(() => {
    const rows = (movementPage?.items || []).filter((r) => r.lpoNumber && linkedLpoNumbers.includes(r.lpoNumber));
    const byDoc = new Map();
    rows.forEach((r) => {
      const existing = byDoc.get(r.documentNumber) || {
        documentNumber: r.documentNumber,
        documentRecordGuid: r.documentRecordGuid,
        documentDate: r.documentDate,
        status: r.status,
        lpoNumber: r.lpoNumber,
        lineCount: 0,
        amount: 0,
      };
      existing.lineCount += 1;
      existing.amount += Number(r.lineAmount || 0);
      byDoc.set(r.documentNumber, existing);
    });
    return [...byDoc.values()];
  }, [movementPage, linkedLpoNumbers]);

  // ---- Summary metrics (History mode) ----
  const summary = useMemo(() => {
    if (!isHistoryMode) return null;
    const submitted = historyRequests.filter((r) => r.status === "SUBMITTED").length;
    const approved = historyRequests.filter((r) => r.status === "APPROVED").length;
    const rejected = historyRequests.filter((r) => r.status === "REJECTED").length;
    const lastDate = historyRequests.reduce((max, r) => (!max || r.requestDate > max ? r.requestDate : max), null);
    // Path A - direct cash ask, real FK to a voucher (see file header comment).
    const totalCash = historyRequests.reduce((sum, r) => {
      const voucher = r.pettyCashVoucherRecordGuid ? vouchersByGuid[r.pettyCashVoucherRecordGuid] : null;
      if (!voucher) return sum;
      return sum + Number(voucher.paidAmount ?? voucher.approvedAmount ?? voucher.requestedAmount ?? 0);
    }, 0);
    // Path B - spares converted to an LPO, real FK. Excludes REJECTED/CANCELLED
    // LPOs (never actually cost anything) - active/approved/received value only.
    const activeLpos = historyLpos.filter((l) => l.status !== "REJECTED" && l.status !== "CANCELLED");
    const totalLpoValue = activeLpos.reduce((sum, l) => sum + Number(l.netAmount || 0), 0);
    return {
      totalRequests: historyRequests.length,
      submitted,
      approved,
      rejected,
      lastDate,
      totalCash,
      totalLpoValue,
      totalCost: totalCash + totalLpoValue,
    };
  }, [isHistoryMode, historyRequests, historyLpos, vouchersByGuid]);

  // ---- Unified History timeline ----
  const timelineRows = useMemo(() => {
    if (!isHistoryMode) return [];
    const rows = [];
    historyRequests.forEach((r) => {
      rows.push({
        key: `req-${r.recordGuid}`,
        date: r.requestDate,
        reference: r.requestNumber,
        source: "MAINTENANCE",
        activity: "Maintenance Request",
        description: r.serviceRepairDescription || r.cashRequestRemarks || "-",
        status: r.status,
        amount: null,
        recordGuid: r.recordGuid,
      });
      if (r.pettyCashVoucherRecordGuid) {
        const voucher = vouchersByGuid[r.pettyCashVoucherRecordGuid];
        rows.push({
          key: `voucher-${r.pettyCashVoucherRecordGuid}`,
          date: voucher?.expenseDate || r.approvedDate || r.requestDate,
          reference: voucher?.voucherNumber || "…",
          source: "PETTY_CASH",
          activity: "Cash Voucher",
          description: voucher?.description || r.cashRequestRemarks || "-",
          status: voucher?.status || "-",
          amount: voucher ? Number(voucher.paidAmount ?? voucher.approvedAmount ?? voucher.requestedAmount ?? 0) : null,
          linkTo: paths.pettyCashVoucherDetail(r.pettyCashVoucherRecordGuid),
        });
      }
      (lposByRequestGuid.get(r.recordGuid) || []).forEach((lpo) => {
        rows.push({
          key: `lpo-${lpo.recordGuid}`,
          date: lpo.lpoDate,
          reference: lpo.lpoNumber,
          source: "LPO",
          activity: "Purchase Order (LPO)",
          description: lpo.purposeDescription || "-",
          status: lpo.status,
          amount: Number(lpo.netAmount || 0),
          linkTo: paths.stockLpoDetail(lpo.recordGuid),
        });
      });
    });
    stockInDocs.forEach((doc) => {
      rows.push({
        key: `stockin-${doc.documentRecordGuid}-${doc.documentNumber}`,
        date: doc.documentDate,
        reference: doc.documentNumber,
        source: "STOCK_IN",
        activity: `Spares Received (${doc.lineCount} item${doc.lineCount === 1 ? "" : "s"})`,
        description: `Against ${doc.lpoNumber}`,
        status: doc.status,
        amount: doc.amount,
        linkTo: paths.stockInDetail(doc.documentRecordGuid),
      });
    });
    return rows.sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [isHistoryMode, historyRequests, lposByRequestGuid, vouchersByGuid, stockInDocs]);

  // ---- Spare Parts History (History mode) - requested vs. LPO-ordered,
  // matched by ProductCode (best-effort, not a guaranteed one-line FK - see
  // project analysis on the spare-item/LPO-line relationship). ----
  const spareRows = useMemo(() => {
    if (!isHistoryMode) return [];
    const rows = [];
    historyRequests.forEach((r) => {
      const detail = requestDetailsByGuid[r.recordGuid];
      (detail?.spareItems || []).forEach((item) => {
        const matchedLpo = (lposByRequestGuid.get(r.recordGuid) || [])
          .map((l) => lpoDetailsByGuid[l.recordGuid])
          .find((full) => full?.lines?.some((line) => String(line.productCode) === String(item.productCode)));
        const matchedLine = matchedLpo?.lines?.find((line) => String(line.productCode) === String(item.productCode));
        rows.push({
          key: `spare-${item.itemId}`,
          requestNumber: r.requestNumber,
          productName: item.productName || item.productCode,
          requestedQty: item.qty,
          unit: item.unit,
          estimatedCost: item.estimatedCost,
          orderedQty: matchedLine?.orderedQty ?? null,
          orderedRate: matchedLine?.unitRate ?? null,
          lpoNumber: matchedLpo?.lpoNumber ?? null,
        });
      });
    });
    return rows;
  }, [isHistoryMode, historyRequests, requestDetailsByGuid, lposByRequestGuid, lpoDetailsByGuid]);

  // ---- Summary totals (flat mode - every vehicle, whatever's currently
  // filtered/visible). Built from data already on hand (the request records'
  // own requestedCashAmount/approvedCashAmount, plus the same lposByRequestGuid
  // map the "LPO Amount" column uses) - no extra fetches, so this stays cheap
  // even across up to 200 rows. Cash here is request-level (approved, or
  // requested if not yet approved), not the voucher's actual paid amount like
  // History mode's totalCash - fetching every voucher for a whole fleet isn't
  // worth the load for a summary tile.
  const flatSummary = useMemo(() => {
    if (isHistoryMode) return null;
    const totalCash = filteredRows.reduce((sum, r) => sum + Number(r.approvedCashAmount ?? r.requestedCashAmount ?? 0), 0);
    const activeLpos = filteredRows.flatMap((r) =>
      (lposByRequestGuid.get(r.recordGuid) || []).filter((l) => l.status !== "REJECTED" && l.status !== "CANCELLED")
    );
    const totalLpoValue = activeLpos.reduce((sum, l) => sum + Number(l.netAmount || 0), 0);
    return { totalRequests: filteredRows.length, totalCash, totalLpoValue, totalCost: totalCash + totalLpoValue };
  }, [isHistoryMode, filteredRows, lposByRequestGuid]);

  const branchCode = availableBranches.find((b) => b.branchId === selectedBranchId)?.branchCode;

  // ---- Flat report columns (no vehicle selected) ----
  const ALL_COLUMNS = [
    { key: "date", label: "Date", dataIndex: "requestDate", render: (v) => (v ? new Date(v).toLocaleDateString() : "-") },
    { key: "requestNo", label: "Request No", dataIndex: "requestNumber" },
    {
      key: "vehicle",
      label: "Vehicle",
      dataIndex: "regNo",
      render: (v, r) => (
        <>
          {v}
          <div className="text-muted small">{r.brand} {r.model}</div>
        </>
      ),
    },
    { key: "branch", label: "Branch", dataIndex: "branchCode" },
    { key: "mileage", label: "Mileage", dataIndex: "currentMileage", render: (v) => v ?? "-" },
    { key: "description", label: "Description", dataIndex: "serviceRepairDescription", render: (v, r) => v || r.cashRequestRemarks || "-" },
    { key: "status", label: "Status", dataIndex: "status", render: (v) => <StatusBadge status={v} /> },
    {
      key: "cash",
      label: "Cash Requested / Approved",
      dataIndex: "requestedCashAmount",
      render: (v, r) => (v == null && r.approvedCashAmount == null ? "-" : `${money(v)} / ${money(r.approvedCashAmount)}`),
    },
    { key: "spares", label: "Spares", dataIndex: "spareItemCount", render: (v) => (v > 0 ? `${v} item${v === 1 ? "" : "s"}` : "-") },
    {
      key: "lpoAmount",
      label: "LPO Amount",
      dataIndex: "recordGuid",
      render: (_, r) => {
        if (!canViewLpo) return "-";
        const linked = (lposByRequestGuid.get(r.recordGuid) || []).filter((l) => l.status !== "REJECTED" && l.status !== "CANCELLED");
        if (linked.length === 0) return "-";
        const total = linked.reduce((s, l) => s + Number(l.netAmount || 0), 0);
        return (
          <>
            {money(total)}
            {linked.length > 1 && <div className="text-muted small">{linked.length} LPOs</div>}
          </>
        );
      },
    },
  ];

  const columns = ALL_COLUMNS.filter((c) => !hiddenColumns.includes(c.key)).map(({ key, label, ...rest }) => ({
    ...rest,
    title: label,
  }));

  const exportColumns = [
    { header: "Date", value: (r) => (r.requestDate ? new Date(r.requestDate).toLocaleDateString() : "-") },
    { header: "Request No", value: (r) => r.requestNumber },
    { header: "Vehicle", value: (r) => `${r.regNo} (${r.brand} ${r.model})` },
    { header: "Branch", value: (r) => r.branchCode },
    { header: "Status", value: (r) => r.status },
    { header: "Cash Requested", value: (r) => money(r.requestedCashAmount), align: "right" },
    { header: "Cash Approved", value: (r) => money(r.approvedCashAmount), align: "right" },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        {isHistoryMode &&
          voucherGuidsNeeded.map((guid) => <VoucherLoader key={guid} recordGuid={guid} onLoaded={onVoucherLoaded} />)}
        {isHistoryMode &&
          requestGuidsNeeded.map((guid) => <RequestDetailLoader key={guid} recordGuid={guid} onLoaded={onRequestDetailLoaded} />)}
        {isHistoryMode &&
          lpoGuidsNeeded.map((guid) => <LpoDetailLoader key={guid} recordGuid={guid} onLoaded={onLpoDetailLoaded} />)}

        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Vehicle Maintenance Report</h4>
              <h6>
                {isHistoryMode
                  ? `Full maintenance history for ${selectedVehicle?.regNo || "this vehicle"}`
                  : "Every maintenance request, across every vehicle, in one place"}
              </h6>
            </div>
          </div>
        </div>

        {!isHistoryMode && flatSummary && (
          <div className="row g-2 mb-3">
            <div className="col-6 col-md-3">
              <InfoTile label="Requests">
                {flatSummary.totalRequests}
                {isTruncated && (
                  <span className="text-muted ms-1 fw-normal" style={{ fontSize: 11 }}>of {totalCount}</span>
                )}
              </InfoTile>
            </div>
            <div className="col-6 col-md-3">
              <InfoTile label="Total Cash (Req./Appr.)" tone="success" value={money(flatSummary.totalCash)} />
            </div>
            <div className="col-6 col-md-3">
              <InfoTile label="Total LPO Value" value={canViewLpo ? money(flatSummary.totalLpoValue) : "-"} />
            </div>
            <div className="col-6 col-md-3">
              <InfoTile label="Total Maintenance Cost" tone="maroon" value={money(flatSummary.totalCost)} />
            </div>
          </div>
        )}

        {isHistoryMode && summary && (
          <div className="row g-2 mb-3">
            <div className="col-6 col-md-2">
              <InfoTile label="Requests">
                {summary.totalRequests}
                <span className="text-muted ms-1 fw-normal" style={{ fontSize: 11 }}>
                  ({summary.approved} appr., {summary.submitted} pend., {summary.rejected} rej.)
                </span>
              </InfoTile>
            </div>
            <div className="col-6 col-md-2">
              <InfoTile label="Last Maintenance" value={summary.lastDate ? new Date(summary.lastDate).toLocaleDateString() : "-"} />
            </div>
            <div className="col-6 col-md-2">
              <InfoTile label="Total Cash" tone="success" value={money(summary.totalCash)} />
            </div>
            <div className="col-6 col-md-2">
              <InfoTile label="Total LPO Value" value={money(summary.totalLpoValue)} />
            </div>
            <div className="col-6 col-md-2">
              <InfoTile label="Total Maintenance Cost" tone="maroon" value={money(summary.totalCost)} />
            </div>
          </div>
        )}

        <div className="card table-list-card">
          <div className="card-body">
            <div className="d-flex flex-column gap-2">
              <div className="d-flex justify-content-start">
                <DateRangePicker preset={preset} setPreset={setPreset} dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo} />
              </div>
              <div className="table-top flex-wrap gap-2" style={{ rowGap: 8 }}>
                <div className="search-set">
                  <div className="search-input">
                    <input
                      type="text"
                      placeholder="Request no., vehicle, description"
                      className="form-control form-control-sm formsearch"
                      style={{ minWidth: 220, height: 34 }}
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                    />
                  </div>
                </div>
                <div className="d-flex gap-2 align-items-center flex-wrap">
                  <VehicleTypeSelect className="form-control form-control-sm" style={{ width: 150, height: 34 }} value={vehicleType} onChange={setVehicleType} allLabel="All Types" />
                  <select className="form-control form-control-sm" style={{ width: 200, height: 34 }} value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}>
                    <option value="">{vehicleType ? `All ${vehicleTypeLabel(vehicleType)} Vehicles` : "All Vehicles"}</option>
                    {pickableVehicles.map((v) => (
                      <option key={v.vehicleId} value={v.vehicleId}>{v.regNo} - {v.brand} {v.model}</option>
                    ))}
                  </select>
                  <select className="form-control form-control-sm" style={{ width: 150, height: 34 }} value={status} onChange={(e) => setStatus(e.target.value)}>
                    {STATUS_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                {!isHistoryMode && (
                  <>
                    <ColumnVisibilityMenu columns={ALL_COLUMNS} hidden={hiddenColumns} onToggle={toggleColumn} />
                    <ListExportToolbar
                      title="Vehicle Maintenance Report"
                      subtitleLines={[`Branch: ${branchCode || "All"}`, `Vehicle type: ${vehicleType ? vehicleTypeLabel(vehicleType) : "All"}`, `Date: ${dateFrom} to ${dateTo}`]}
                      branchCode={branchCode}
                      columns={exportColumns}
                      rows={filteredRows}
                      fileName="VehicleMaintenanceReport"
                    />
                  </>
                )}
              </div>
            </div>

            {!isHistoryMode && isTruncated && (
              <div className="alert alert-warning py-2 px-3 mb-2 mt-2">
                Showing the first {requests.length} of {totalCount} matching requests - narrow the date range or filters for a complete report.
              </div>
            )}

            {!isHistoryMode && (
              <div className="table-responsive mt-2">
                <DataTable
                  columns={columns}
                  dataSource={filteredRows}
                  rowKey="recordGuid"
                  loading={isLoading}
                  expandable={{ expandedRowRender: (record) => <ExpandedRequestRow recordGuid={record.recordGuid} /> }}
                />
              </div>
            )}
          </div>
        </div>

        {isHistoryMode && (
          <div className="card table-list-card">
            <div className="card-body">
              <h5 className="mb-3 d-inline-flex align-items-center gap-2"><Truck size={18} /> History Timeline</h5>
              <div className="table-responsive">
                <DataTable
                  columns={[
                    { title: "Date", dataIndex: "date", render: (v) => (v ? new Date(v).toLocaleDateString() : "-") },
                    {
                      title: "Source",
                      dataIndex: "source",
                      render: (v) => {
                        const meta = SOURCE_BADGE[v] || { label: v, cls: "badge-linesecondary" };
                        return <span className={`badge ${meta.cls}`}>{meta.label}</span>;
                      },
                    },
                    {
                      title: "Reference",
                      dataIndex: "reference",
                      render: (v, r) => (r.linkTo ? <Link to={r.linkTo}>{v}</Link> : v),
                    },
                    { title: "Activity", dataIndex: "activity" },
                    { title: "Description", dataIndex: "description" },
                    { title: "Status", dataIndex: "status", render: (v) => (v && v !== "-" ? <StatusBadge status={v} /> : "-") },
                    { title: "Amount", dataIndex: "amount", render: (v) => (v == null ? "—" : money(v)) },
                  ]}
                  dataSource={timelineRows}
                  rowKey="key"
                  loading={isLoading}
                  expandable={{
                    rowExpandable: (r) => r.source === "MAINTENANCE",
                    expandedRowRender: (r) => <ExpandedRequestRow recordGuid={r.recordGuid} />,
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {isHistoryMode && spareRows.length > 0 && (
          <div className="card table-list-card">
            <div className="card-body">
              <h5 className="mb-3 d-inline-flex align-items-center gap-2"><Package size={18} /> Spare Parts History</h5>
              <div className="table-responsive">
                <DataTable
                  columns={[
                    { title: "Part", dataIndex: "productName" },
                    { title: "Request No", dataIndex: "requestNumber" },
                    { title: "Requested Qty", dataIndex: "requestedQty", render: (v, r) => `${v ?? "-"} ${r.unit || ""}` },
                    { title: "Est. Cost", dataIndex: "estimatedCost", render: (v) => money(v) },
                    { title: "LPO", dataIndex: "lpoNumber", render: (v) => v || "-" },
                    { title: "Ordered Qty", dataIndex: "orderedQty", render: (v) => v ?? "-" },
                    { title: "Ordered Rate", dataIndex: "orderedRate", render: (v) => money(v) },
                  ]}
                  dataSource={spareRows}
                  rowKey="key"
                  loading={isLoading}
                />
              </div>
            </div>
          </div>
        )}

        {!canViewLpo && (
          <div className="alert alert-light border small mt-2">
            <CreditCard size={14} className="me-1" />
            LPO amounts are hidden - viewing them requires the Stock.ViewLpo permission.
          </div>
        )}
      </div>
    </div>
  );
};

export default VehicleMaintenanceReportPage;
